<script lang="ts">
import { computed, defineComponent, h, ref, unref } from 'vue';

import { classNames, filterEmpty } from '@components/utils';

import { makeDisabledProps, makeThemeProps, provideTheme, useRender } from '../../composable';

import { makeTreeEventsProps, makeTreeProps } from './composable';
import TreeProvider from './components/TreeProvider';
import { renderSwitcherIcon } from './components/SwitcherIcon';
import DropIndicator from './components/DropIndicator';

import type { NTree } from './interface';
import type { ScrollTo } from './components/VirtualList';
import type { SwitcherIconProps } from './components/SwitcherIcon';
import type { ITreeProps } from './composable';

export const componentName = 'CdsTree';
export default defineComponent({
  name: componentName,
  inheritAttrs: false,
  props: {
    ...makeTreeProps(),
    ...makeTreeEventsProps(),
    ...makeDisabledProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие выбора узла (v-model)
     */
    'update:selectedKeys': (selectedKeys: NTree.TKey[]) => true,
    /**
     * Событие выбора узла по чекбоксу (v-model)
     */
    'update:checkedKeys': (checkedKeys: NTree.TKey[] | { checked: NTree.TKey[]; halfChecked: NTree.TKey[] }) => true,
    /**
     * Событие раскрытия узла (v-model)
     */
    'update:expandedKeys': (expandedKeys: NTree.TKey[]) => true,
    /**
     * Событие клика по узлу
     * @type { event: MouseEvent, node: NTree.IEventDataNode }
     */
    click: (event: MouseEvent, node: NTree.IEventDataNode) => true,
    /**
     * Событие выбора узла
     * @type { checked: { checked: NTree.TKey[]; halfChecked: NTree.TKey[] } | NTree.TKey[], info: NTree.ICheckInfo }
     */
    check: (checked: { checked: NTree.TKey[]; halfChecked: NTree.TKey[] } | NTree.TKey[], info: NTree.ICheckInfo) =>
      true,
    /**
     * Событие начала перетаскивания узла
     * @type { info: NTree.TNodeDragEventParams }
     */
    dragstart: (info: NTree.TNodeDragEventParams) => true,
    /**
     * Событие окончания перетаскивания узла
     * @type { info: NTree.TNodeDragEventParams }
     */
    dragend: (info: NTree.TNodeDragEventParams) => true,
    /**
     * Событие входа узла в зону перетаскивания
     */
    dragenter: (info: NTree.TNodeDragEventParams & { expandedKeys: NTree.TKey[] }) => true,
    /**
     * Событие выхода узла из зоны перетаскивания
     * @type { info: NTree.TNodeDragEventParams }
     */
    dragleave: (info: NTree.TNodeDragEventParams) => true,
    /**
     * Событие перемещения узла в зоне перетаскивания
     * @type { info: NTree.TNodeDragEventParams }
     */
    dragover: (info: NTree.TNodeDragEventParams) => true,
    /**
     * Событие перемещения узла
     */
    drop: (
      info: NTree.TNodeDragEventParams & {
        dragNode: NTree.IEventDataNode;
        dragNodesKeys: NTree.TKey[];
        dragPosition: number;
        dropToGap: boolean;
      }
    ) => true,
    /**
     * Событие раскрытия узла
     * @type { expandedKeys: NTree.TKey[], info: { node: NTree.TEventHandler; expanded: boolean; nativeEvent: MouseEvent } }
     */
    expand: (
      expandedKeys: NTree.TKey[],
      info: { node: NTree.IEventDataNode; expanded: boolean; nativeEvent: MouseEvent }
    ) => true,
    /**
     * Событие выбора узла
     * @type { selectedKeys: NTree.TKey, info: { event: 'select'; selected: boolean; node: NTree.IEventDataNode; selectedNodes: NTree.IDataNode[]; nativeEvent: MouseEvent; } }
     */
    select: (
      selectedKeys: NTree.TKey[],
      info: {
        event: 'select';
        selected: boolean;
        node: NTree.IEventDataNode;
        selectedNodes: NTree.IDataNode[];
        nativeEvent: MouseEvent;
      }
    ) => true,
    /**
     * Событие загрузки узла
     * @type { treeNode: NTree.IEventDataNode }
     */
    load: (treeNode: NTree.IEventDataNode) => true,
    /**
     * Событие правого клика по узлу
     * @type { info: { event: MouseEvent; node: NTree.IEventDataNode } }
     */
    rightClick: (info: { event: MouseEvent; node: NTree.IEventDataNode }) => true,

    /**
     * Событие двойного клика по узлу
     * @type { event: MouseEvent; node: NTree.IEventDataNode }
     */
    dblclick: (event: MouseEvent, node: NTree.IEventDataNode) => true,
    /**
     * Событие двойного клика по узлу
     * @type { event: MouseEvent; node: NTree.IEventDataNode }
     */
    doubleclick: (event: MouseEvent, node: NTree.IEventDataNode) => true
  },
  setup(props, { attrs, expose, emit, slots }) {
    const treeRef = ref<typeof TreeProvider>();
    const { themeClasses } = provideTheme(props);

    const scrollTo: ScrollTo = (scroll) => {
      unref(treeRef)?.scrollTo(scroll);
    };

    const handleClick = (event: MouseEvent, node: NTree.IEventDataNode) => {
      if (props.expandAction === 'click') {
        unref(treeRef)?.onNodeExpand(event, node);
      }

      emit('click', event, node);
    };

    const handleDoubleClick = (event: MouseEvent, node: NTree.IEventDataNode) => {
      if (props.expandAction === 'dblclick' || props.expandAction === 'doubleclick') {
        unref(treeRef)?.onNodeExpand(event, node);
      }

      emit('dblclick', event, node);
      emit('doubleclick', event, node);
    };

    const handleCheck: ITreeProps['onCheck'] = (checkedObjOrKeys, eventObj) => {
      emit('update:checkedKeys', checkedObjOrKeys);
      emit('check', checkedObjOrKeys, eventObj);
    };

    const handleExpand: ITreeProps['onExpand'] = (expandedKeys, eventObj) => {
      emit('update:expandedKeys', expandedKeys);
      emit('expand', expandedKeys, eventObj);
    };

    const handleSelect: ITreeProps['onSelect'] = (selectedKeys, eventObj) => {
      emit('update:selectedKeys', selectedKeys);
      emit('select', selectedKeys, eventObj);
    };

    useRender(() => {
      const {
        showIcon,
        showLine,
        blockNode,
        checkable,
        selectable,
        fieldNames,
        openAnimation,
        virtual,
        icon = slots.icon,
        switcherIcon = slots.switcherIcon
      } = props;

      const children = slots.default ? filterEmpty(slots.default()) : undefined;

      return h(
        TreeProvider,
        {
          ...attrs,
          ...props,
          ref: treeRef,
          showLine: !!showLine,
          fieldNames,
          icon,
          virtual,
          openAnimation,
          checkable,
          selectable,
          children,
          class: classNames(
            {
              'cds-tree--icon-hide': !showIcon,
              'cds-tree--block-node': blockNode,
              'cds-tree--unselectable': !selectable,
              'cds-tree--uncheckable': !checkable,
              'cds-tree--clickable-node': !!props.expandAction
            },
            unref(themeClasses),
            attrs.class
          ),
          dropIndicatorRender: DropIndicator,
          switcherIcon: (nodeProps: SwitcherIconProps) => renderSwitcherIcon(switcherIcon, showLine, nodeProps),
          onClick: handleClick,
          onCheck: handleCheck,
          onExpand: handleExpand,
          onSelect: handleSelect,
          onDblclick: handleDoubleClick
        },
        {
          ...slots,
          checkable: () => h('span', { class: classNames('cds-tree__checkbox-inner') })
        }
      );
    });

    expose({
      treeRef,
      onNodeExpand: (...args: any[]) => {
        unref(treeRef)?.onNodeExpand(...args);
      },
      scrollTo,
      selectedKeys: computed(() => unref(treeRef)?.selectedKeys),
      checkedKeys: computed(() => unref(treeRef)?.checkedKeys),
      halfCheckedKeys: computed(() => unref(treeRef)?.halfCheckedKeys),
      expandedKeys: computed(() => unref(treeRef)?.expandedKeys),
      loadingKeys: computed(() => unref(treeRef)?.loadingKeys),
      loadedKeys: computed(() => unref(treeRef)?.loadedKeys)
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsTree';
</style>
